import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { IItemsListAction2 } from '@fi-app/shared/fi-items-list/fi-items-list';
import { Animations } from '@fi-shared/utils/animations';
import { IOnboardingCompletionData } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { IOnboardingActive, IOnboardingActiveData } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiOnboardingCompletionActionsBuilderService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-actions-builder.service';
import { findClosest } from '@fi-shared/utils';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';

@Component({
    selector: 'fi-multiple-onboardings-card',
    templateUrl: './fi-multiple-onboardings-card.component.html',
    styleUrl: './fi-multiple-onboardings-card.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-multiple-onboardings-card' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiMultipleOnboardingsCardComponent {
    public onboardingActiveData = input.required<IOnboardingActiveData>();
    public visibleOnboardings = input.required<IOnboardingActive[]>();
    public currentDispalyedOnboardingCompletion = input.required<IOnboardingCompletionData>();
    public isLoading = input<boolean>();

    public displayOnboardingCompletion = output<number>();
    public resetCurrentDisplayedOnboardingCompletion = output<void>();

    private fiOnboardingCompletionActionsBuilderService = inject(FiOnboardingCompletionActionsBuilderService);
    private elementRef = inject(ElementRef<HTMLElement>);
    private fiScrollToElementAnimateService = inject(FiScrollToElementAnimateService);

    protected isExpanded = signal(false);

    protected incompleteOnboardingsCount = computed(() => {
        return this.visibleOnboardings().filter((onboarding) => onboarding.Status !== FiOnboardingStatusType.Completed)
            .length;
    });

    protected actions = computed<IItemsListAction2<IOnboardingActive>[]>(() => {
        const onboardingIds = this.visibleOnboardings()?.map((activeOnboarding) => activeOnboarding.Id) ?? [];
        return this.fiOnboardingCompletionActionsBuilderService.getHomeOnboardingMultipleActiveOnboardingsActions(
            onboardingIds,
        );
    });

    protected currentOnboardingCompletionActions = computed<IItemsListAction2[]>(() => {
        return this.fiOnboardingCompletionActionsBuilderService.getHomeOnboardingSingleSelectedOnboardingCompletionActions(
            {
                userCompletion: this.currentDispalyedOnboardingCompletion().UserCompletion,
            },
        );
    });

    private currentUserCompletionEffect = effect(() => {
        const currentUserCompletion = this.currentDispalyedOnboardingCompletion();
        if (currentUserCompletion) {
            setTimeout(() => {
                this.fiScrollToElementAnimateService.scrollToElementIntoView(
                    this.elementRef.nativeElement,
                    this.getHomePageScrollElement(),
                    {
                        speed: 5000,
                    },
                );
            });
        }
    });

    private getHomePageScrollElement(): HTMLElement {
        return findClosest(this.elementRef.nativeElement, '#start-page-layout');
    }

    protected toggleExpansionState($event: MouseEvent): void {
        $event.stopPropagation();
        this.isExpanded.set(!this.isExpanded());
    }

    protected onSignleOnboardingCardBackButtonClick(): void {
        this.isExpanded.set(true);
        this.resetCurrentDisplayedOnboardingCompletion.emit();
    }
}
